import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useBlocker, useParams } from "react-router";
import { useMutation } from "@apollo/client";
import type { SectionType } from "@sections/registry";
import { RenderContextProvider } from "@sections/RenderContext";
import { useToast } from "../../../components/ui/Toast";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { usePageData } from "../pages/usePageData";
import { useEditorState } from "./useEditorState";
import { EditorTopBar } from "./EditorTopBar";
import { OutlineRail } from "./OutlineRail";
import { InspectorPanel } from "./InspectorPanel";
import { EditorCanvas, type EditorCanvasHandle, useCanvasRenderContext } from "./EditorCanvas";
import { SectionLibrary } from "./SectionLibrary";
import { PageSettings } from "./PageSettings";
import { VersionHistory, type PageVersion } from "./VersionHistory";
import { SECTION_LABELS } from "./constants";
import {
  ROLLBACK_PAGE_TO_VERSION,
  evictPageListings,
} from "../pages/mutations";
import type { PageContentRow, PageContent } from "../pages/types";

/**
 * Inner editor, rendered once page data is loaded.
 * Separated so useEditorState initializes with real data.
 */
function CanvasEditorInner({
  page,
}: {
  page: NonNullable<ReturnType<typeof usePageData>["page"]>;
}) {
  const editor = useEditorState(page.id, page.draftContent);
  const toast = useToast();
  const canvasRef = useRef<EditorCanvasHandle>(null);
  const renderContext = useCanvasRenderContext();

  // Section Library state
  const [showLibrary, setShowLibrary] = useState(false);
  const [insertAtIndex, setInsertAtIndex] = useState(0);

  // Page Settings state
  const [showSettings, setShowSettings] = useState(false);

  // Version history state
  const [showHistory, setShowHistory] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );
  const [previewingVersion, setPreviewingVersion] = useState<{
    content: PageContentRow[];
    version: number;
    label: string;
  } | null>(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const pendingRestoreRef = useRef<PageVersion | null>(null);

  const [rollbackMutation, { loading: isRestoring }] = useMutation(
    ROLLBACK_PAGE_TO_VERSION,
    { update: evictPageListings },
  );

  const openLibrary = useCallback((atIndex: number) => {
    setInsertAtIndex(atIndex);
    setShowLibrary(true);
  }, []);

  const closeLibrary = useCallback(() => {
    setShowLibrary(false);
  }, []);

  const handleScrollToSection = useCallback((sectionId: string) => {
    canvasRef.current?.scrollToSection(sectionId);
  }, []);

  const toggleSettings = useCallback(() => {
    setShowSettings((prev) => {
      const next = !prev;
      // When settings opens, close inspector
      if (next) {
        editor.selectSection(null);
      }
      return next;
    });
  }, [editor]);

  // Close settings when inspector opens (section selected)
  useEffect(() => {
    if (editor.selectedSectionId !== null && showSettings) {
      setShowSettings(false);
    }
  }, [editor.selectedSectionId, showSettings]);

  // Version history helpers

  const toggleHistory = useCallback(() => {
    setShowHistory((prev) => {
      const next = !prev;
      if (next) {
        // When opening history, close inspector and settings
        editor.selectSection(null);
        setShowSettings(false);
      } else {
        // When closing history, clear version preview
        setPreviewingVersion(null);
        setSelectedVersionId(null);
      }
      return next;
    });
  }, [editor]);

  const exitHistory = useCallback(() => {
    setShowHistory(false);
    setPreviewingVersion(null);
    setSelectedVersionId(null);
  }, []);

  // Close history when inspector opens (section selected)
  useEffect(() => {
    if (editor.selectedSectionId !== null && showHistory) {
      exitHistory();
    }
  }, [editor.selectedSectionId, showHistory, exitHistory]);

  const handleSelectVersion = useCallback((version: PageVersion) => {
    setSelectedVersionId(version.id);
    // Parse the version content for canvas preview
    try {
      const parsed: PageContent = JSON.parse(version.content);
      const rows: PageContentRow[] = (parsed.content || []).map((row, idx) => ({
        ...row,
        id: row.id || `section-${idx}`,
        enabled: row.enabled !== false,
      }));
      const timeStr = new Date(version.createdAt).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      const dateObj = new Date(version.createdAt);
      const now = new Date();
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      let dateLabel: string;
      if (dateObj >= todayStart) {
        dateLabel = `today, ${timeStr}`;
      } else {
        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        if (dateObj >= yesterdayStart) {
          dateLabel = `yesterday, ${timeStr}`;
        } else {
          const monthDay = dateObj.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
          dateLabel = `${monthDay}, ${timeStr}`;
        }
      }
      setPreviewingVersion({
        content: rows,
        version: version.version,
        label: `Version from ${dateLabel}`,
      });
    } catch {
      // If content can't be parsed, clear the preview
      setPreviewingVersion(null);
    }
  }, []);

  const handleRestoreRequest = useCallback(
    async (version: PageVersion) => {
      pendingRestoreRef.current = version;
      // If there are unsaved changes, warn the user first
      if (editor.isDirty) {
        setShowUnsavedWarning(true);
        return;
      }
      setShowRestoreConfirm(true);
    },
    [editor.isDirty],
  );

  const executeRestore = useCallback(async () => {
    const version = pendingRestoreRef.current;
    if (!version) return;

    setShowRestoreConfirm(false);
    setShowUnsavedWarning(false);

    try {
      const result = await rollbackMutation({
        variables: { pageId: page.id, versionId: version.id },
      });
      const restored = result.data?.rollbackPageToVersion;
      if (!restored) throw new Error("No data returned");

      // Parse the restored draft content and load it into the editor
      const restoredDraft = (restored as { draftContent?: string | null })
        .draftContent;
      if (restoredDraft) {
        try {
          const parsed: PageContent = JSON.parse(restoredDraft);
          const rows: PageContentRow[] = (parsed.content || []).map(
            (row, idx) => ({
              ...row,
              id: row.id || `section-${idx}`,
              enabled: row.enabled !== false,
            }),
          );
          editor.setContent(rows);
        } catch {
          // Content parse failure -- still close history
        }
      }

      // Exit history mode
      exitHistory();

      toast.push({
        kind: "success",
        title: `Restored to version ${version.version}`,
        description: "Review and publish when ready.",
      });
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Couldn't restore version",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
    pendingRestoreRef.current = null;
  }, [rollbackMutation, page.id, editor, exitHistory, toast]);

  // Wrap editor actions with toasts
  const editorWithToasts = {
    ...editor,
    saveDraft: async () => {
      const result = await editor.saveDraft();
      if (result.success) {
        toast.push({ kind: "success", title: "Draft saved" });
      } else {
        toast.push({
          kind: "error",
          title: "Couldn't save draft",
          description: result.error,
        });
      }
      return result;
    },
    publish: async () => {
      const result = await editor.publish();
      if (result.success) {
        toast.push({ kind: "success", title: "Published" });
      } else {
        toast.push({
          kind: "error",
          title: "Couldn't publish",
          description: result.error,
        });
      }
      return result;
    },
    revertToPublished: async () => {
      const result = await editor.revertToPublished();
      if (result.success) {
        toast.push({
          kind: "success",
          title: "Reverted to published version",
        });
      } else {
        toast.push({
          kind: "error",
          title: "Couldn't revert",
          description: result.error,
        });
      }
      return result;
    },
  };

  // --- Keyboard shortcuts ---
  // Cmd/Ctrl+Z = undo, Cmd/Ctrl+Shift+Z = redo, Cmd/Ctrl+S = save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      // Don't intercept undo/redo when a contenteditable or input is focused
      // (let TipTap / native inputs handle their own undo)
      const active = document.activeElement;
      const isEditing =
        active instanceof HTMLElement &&
        (active.isContentEditable ||
          active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA");

      if (e.key === "s") {
        // Cmd+S always saves the draft, even from inputs
        e.preventDefault();
        if (editor.isDirty) {
          void editorWithToasts.saveDraft();
        }
      } else if (!isEditing && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        editor.undo();
      } else if (!isEditing && e.key === "z" && e.shiftKey) {
        e.preventDefault();
        editor.redo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editor, editorWithToasts]);

  // --- beforeunload guard (browser close/reload) ---
  useEffect(() => {
    if (!editor.isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [editor.isDirty]);

  // --- React Router navigation blocker ---
  const blocker = useBlocker(editor.isDirty);

  const handleLibraryInsert = useCallback(
    (type: SectionType, defaultProps: Record<string, unknown>) => {
      const newId = editorWithToasts.addSection(type, defaultProps, insertAtIndex);
      closeLibrary();
      // Select the new section and scroll to it after it renders
      editorWithToasts.selectSection(newId);
      requestAnimationFrame(() => {
        canvasRef.current?.scrollToSection(newId);
      });
    },
    [editorWithToasts, insertAtIndex, closeLibrary],
  );

  // Compute the human-readable insertion-point label for SectionLibrary
  const insertAfterLabel = useMemo(() => {
    const content = editorWithToasts.content;
    if (content.length === 0) return undefined;
    if (insertAtIndex <= 0) return "at the beginning";
    if (insertAtIndex >= content.length) return "at the end";
    const prev = content[insertAtIndex - 1];
    const label = SECTION_LABELS[prev.type] || prev.type;
    return `after ${label}`;
  }, [editorWithToasts.content, insertAtIndex]);

  // The back link just navigates normally. If the editor is dirty,
  // React Router's useBlocker intercepts and shows the styled
  // ConfirmDialog -- no window.confirm() needed.

  // When previewing a version, create a read-only editor proxy that shows
  // the version content instead of the live draft.
  const previewEditor = useMemo(() => {
    if (!previewingVersion) return null;
    return {
      ...editorWithToasts,
      content: previewingVersion.content,
      isDirty: false,
      selectedSectionId: null,
      // Disable all mutations in preview mode
      selectSection: () => {},
      updateSection: () => {},
      updateSectionProp: () => {},
      updateSectionSilent: () => {},
      updateSectionPropSilent: () => {},
      commitUndoCheckpoint: () => {},
      addSection: () => "",
      removeSection: () => {},
      moveSection: () => {},
      reorderSection: () => {},
      toggleSectionVisibility: () => {},
      duplicateSection: () => {},
      setContent: () => {},
    };
  }, [previewingVersion, editorWithToasts]);

  const activeEditor = previewEditor ?? editorWithToasts;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <EditorTopBar
        page={page}
        editor={editorWithToasts}
        showSettings={showSettings}
        onToggleSettings={toggleSettings}
        showHistory={showHistory}
        onToggleHistory={toggleHistory}
        previewingVersion={previewingVersion?.version ?? null}
        previewingLabel={previewingVersion?.label}
        onExitHistory={exitHistory}
      />

      <RenderContextProvider value={renderContext}>
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {!showHistory && (
            <OutlineRail
              editor={editorWithToasts}
              onScrollToSection={handleScrollToSection}
              onOpenLibrary={() => {
                // Insert after the selected section, or at the end
                const selectedIdx = editorWithToasts.selectedSectionId
                  ? editorWithToasts.content.findIndex(
                      (r) => r.id === editorWithToasts.selectedSectionId,
                    )
                  : -1;
                const insertIdx =
                  selectedIdx >= 0
                    ? selectedIdx + 1
                    : editorWithToasts.content.length;
                openLibrary(insertIdx);
              }}
            />
          )}
          <EditorCanvas
            ref={canvasRef}
            page={page}
            editor={activeEditor}
            onOpenLibrary={showHistory ? () => {} : openLibrary}
            readOnly={!!previewingVersion}
          />
          {showHistory ? (
            <VersionHistory
              page={page}
              open={showHistory}
              selectedVersionId={selectedVersionId}
              onSelectVersion={handleSelectVersion}
              onRestore={handleRestoreRequest}
              onClose={exitHistory}
            />
          ) : showSettings ? (
            <PageSettings
              page={page}
              open={showSettings}
              onClose={() => setShowSettings(false)}
            />
          ) : (
            <InspectorPanel
              editor={editorWithToasts}
              onOpenLibrary={() => openLibrary(editorWithToasts.content.length)}
            />
          )}
        </div>
      </RenderContextProvider>

      <SectionLibrary
        open={showLibrary}
        onClose={closeLibrary}
        onInsert={handleLibraryInsert}
        insertAfterLabel={insertAfterLabel}
      />

      {/* React Router blocker confirmation dialog */}
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title="Unsaved changes"
        description="You have unsaved changes that will be lost if you leave this page."
        variant="danger"
        confirmLabel="Leave"
        cancelLabel="Stay"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />

      {/* Unsaved changes warning before restore */}
      <ConfirmDialog
        open={showUnsavedWarning}
        title="You have unsaved changes"
        description="Your draft has edits that haven't been saved. Restoring a version now would lose them. Save your draft first, or discard the unsaved edits."
        variant="danger"
        confirmLabel="Discard edits and restore"
        cancelLabel="Cancel"
        onConfirm={() => {
          setShowUnsavedWarning(false);
          setShowRestoreConfirm(true);
        }}
        onCancel={() => {
          setShowUnsavedWarning(false);
          pendingRestoreRef.current = null;
        }}
      />

      {/* Restore confirmation */}
      <ConfirmDialog
        open={showRestoreConfirm}
        title="Restore this version?"
        description={
          <>
            Your draft will be replaced with this version. The live site is{" "}
            <b>not</b> affected -- review the restored draft, then publish when
            you are ready. Your current draft is saved as a version first, so
            you can undo this restore.
          </>
        }
        confirmLabel={isRestoring ? "Restoring..." : "Restore to draft"}
        cancelLabel="Cancel"
        onConfirm={() => void executeRestore()}
        onCancel={() => {
          setShowRestoreConfirm(false);
          pendingRestoreRef.current = null;
        }}
        confirming={isRestoring}
      />
    </div>
  );
}

/**
 * Canvas page editor route component.
 * Handles loading/error states before rendering the editor.
 */
export function PageCanvasEditor() {
  const { id } = useParams<{ id: string }>();
  const { page, loading, error } = usePageData(id);

  if (loading && !page) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[13px] text-ink-4">Loading page...</div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="px-7 py-10">
        <div className="mb-3 text-[13px] text-rust">
          Couldn&apos;t load page: {error?.message ?? "not found"}
        </div>
        <Link
          to="/website/pages"
          className="text-[13px] text-tide hover:underline"
        >
          &larr; Back to pages
        </Link>
      </div>
    );
  }

  return <CanvasEditorInner page={page} />;
}
